import {
  lazy,
  Suspense,
  useRef,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useAtom, useAtomValue } from "jotai";
import timezone from "dayjs/plugin/timezone";
import { flushSync } from "react-dom";
import classNames from "classnames";
import Cookies from "js-cookie";
import dayjs from "dayjs";

import GalleryPreviewModal from "../../../views/library/LibraryComponents/FileInformation/GalleryPreviewModal";
import useUpdateFile from "../../../api/api-hooks/images/useUpdateFile";
import BrieConversationAPI from "../../../api/api/BrieConversationAPI";
import { modelAtom, tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { NewConversationMessage } from "./NewConversationMessage";
import { ReferenceValue } from "../../../types/TenantInterfaces";
import useRecord from "../../../api/api-hooks/records/useRecord";
import { InvalidateGPTProjectQueries } from "./useCreateProject";
import { sidebarOpenAtom } from "../../../atoms/SidebarAtoms";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { MetadataFile } from "../../../types/FileInterfaces";
import { BrieGPTTabs } from "./BrieGPTSidebarElements/Types";
import { FileRecord } from "../../../views/library/Library";
import { headerOpenAtom } from "../../../atoms/PageAtoms";
import useUser from "../../../api/api-hooks/auth/useUser";
import { newToastEvent } from "../../../utils/ToastUtil";
import { Page } from "../../../types/GlobalInterfaces";
import MembersList from "./ProjectMembers/MembersList";
import AppSpinner from "../../../elements/AppSpinner";
import { View } from "../../../types/PageInterfaces";
import useBrieGPTProject from "./useBrieGPTProject";
import { useBrieGPTChats } from "./useBrieGPTItems";
import TenantAPI from "../../../api/api/TenantAPI";
import useChatMessages from "./useChatMessages";
import useScrollButton from "./useScrollButton";
import BrieGPTSidebar from "./BrieGPTSidebar";
import { IsMobileAtom } from "../../../App";
import BrieGPTInput from "./BrieGPTInput";
import ProjectHome from "./ProjectHome";

const BrieComment = lazy(() => import("./BrieComment"));
dayjs.extend(timezone);

interface Props {
  view: View;
  page: Page;
  viewLoading: boolean;
}

export interface BrieGPTMessagePayload {
  ModelName: string;
  Content: string;
  Role: string;
  ViewID?: string;
  Record?: ReferenceValue;
  Field?: string;
  ChatID: string;
  Attachments?: any;
  CreatedBy: ReferenceValue;
}

export interface CustomFile {
  file: File;
  uploading: boolean;
  uploadID: string;
  error?: string;
  uploadedFileData?: any;
}

function PageViewBrieGPT({ view }: Props) {
  const { user } = useUser();
  const [params, setParams] = useSearchParams();
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const chatID = params.get("chatID");
  const projectID = params.get("projectID");

  // File Preview Management (In Chat Messages, BrieGPTInput is separate.)
  const [openFile, setOpenFile] = useState<{ File: MetadataFile } | null>(null);
  const [openInfo, setOpenInfo] = useState<FileRecord | null>(null);
  const { updateFile, updatingFile } = useUpdateFile();

  const isMobile = useAtomValue(IsMobileAtom);
  const [open, setOpen] = useState(isMobile ? false : true);
  const sidebarOpen = useAtomValue(sidebarOpenAtom);

  const [processing, setProcessing] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [model, setModel] = useAtom(modelAtom);
  const [tab, setTab] = useState<BrieGPTTabs>("Personal");

  const { record } = useRecord({
    moduleAPIName: "Contacts",
    recordID: user?.id,
    enabled: !!user,
    fields: ["id", "_RecordName"],
  });
  const {
    messagesPages,
    messagesLoading,
    fetchNextPage,
    hasNextPage,
    fetching,
    handleMessageUpdate,
  } = useChatMessages({
    type: "view",
    viewID: view?.id,
    chatID: chatID,
    processing: processing,
  });
  const { project } = useBrieGPTProject(projectID);
  const { chats } = useBrieGPTChats({
    type: "view",
    viewID: view?.id,
  });

  const [files, setFiles] = useState<CustomFile[]>([]);

  const messages = messagesPages.flatMap((page) => page.messages);
  const tenantID = useAtomValue(tenantIDAtom) || "";
  const headerOpen = useAtomValue(headerOpenAtom);
  const client = useQueryClient();
  const messagesRef = useRef<BrieGPTMessagePayload[]>(messages);
  const observer = useRef<IntersectionObserver | null>(null);

  // Add refs for scroll position management
  const prevMessagesLengthRef = useRef(messages.length);
  const shouldPreserveScrollRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const hasInitiallyScrolledRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  messagesRef.current = messages;

  const { containerRef, messagesEndRef, isAtBottom, scrollToBottom } =
    useScrollButton(processing, [messages, responseMessage]);

  // Reset initial load flag when chatID changes
  useEffect(() => {
    isInitialLoadRef.current = true;
    hasInitiallyScrolledRef.current = false;
  }, [chatID]);

  const controllerRef = useRef<AbortController | null>();

  // Effect to handle scroll position preservation when new messages are loaded
  useEffect(() => {
    if (containerRef.current && shouldPreserveScrollRef.current) {
      const container = containerRef.current;
      const currentScrollHeight = container.scrollHeight;
      const scrollHeightDiff =
        currentScrollHeight - prevScrollHeightRef.current;

      if (scrollHeightDiff > 0) {
        container.scrollTop = container.scrollTop + scrollHeightDiff;
      }

      shouldPreserveScrollRef.current = false;
    }
  }, [messages.length]);

  // FIXED: Effect to handle initial scroll to bottom - key changes here
  useEffect(() => {
    // Only scroll on initial load when messages are loaded and we haven't scrolled yet
    if (
      !messagesLoading &&
      messages.length > 0 &&
      isInitialLoadRef.current &&
      containerRef.current &&
      !hasInitiallyScrolledRef.current
    ) {
      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(() => {
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
          hasInitiallyScrolledRef.current = true;
          isInitialLoadRef.current = false;
        }
      });
    }
  }, [messagesLoading, messages.length]); // Added messagesLoading to dependencies

  // Effect to track when we should preserve scroll position
  useEffect(() => {
    const currentLength = messages.length;
    const prevLength = prevMessagesLengthRef.current;

    // If messages increased and we're not at the bottom, preserve scroll
    if (
      currentLength > prevLength &&
      !isAtBottom &&
      containerRef.current &&
      !isInitialLoadRef.current
    ) {
      shouldPreserveScrollRef.current = true;
      prevScrollHeightRef.current = containerRef.current.scrollHeight;
    }

    prevMessagesLengthRef.current = currentLength;
  }, [messages.length, isAtBottom]);

  const handleSendMessage = useCallback(
    async (providedMessage?: string) => {
      let message =
        typeof providedMessage === "string" && !!providedMessage
          ? providedMessage
          : messageRef.current?.value;

      if (processing || !message) return;

      if (files?.length > 0) {
        for (const file of files) {
          if (file.uploading || !file.uploadedFileData) {
            return newToastEvent(
              "error",
              "Please wait for all files finish uploading."
            );
          }
        }
      }
      controllerRef.current = new AbortController();
      const { signal } = controllerRef.current;

      const finalModel = model;

      let finalChatID = chatID;

      const messageAttachments = files.map((f) => {
        const transcription = f?.uploadedFileData?.Transcript;

        return {
          id: f?.uploadedFileData?.id,
          File: f?.uploadedFileData?.File,
          AltText: "File",
          Name: f?.file?.name,
          Size: f?.file?.size,
          Type: f?.file?.type,
          UploadedDate:
            f?.uploadedFileData?.UploadedDate || new Date().toISOString(),
          UploadedBy: f?.uploadedFileData?.UploadedBy || user?.id,
          RecordID: f?.uploadedFileData?.RecordID,
          // Uploaded files are indexed by default for this BRIE GPT feature.
          ...(transcription ? { Transcript: transcription } : { Index: true }),
        };
      });

      const newMessage = {
        Content: message,
        Role: "user",
        ChatID: chatID || "",
        ViewID: view?.id,
        ModelName: model,
        ...(messageAttachments?.length > 0 && {
          Attachments: messageAttachments,
        }),
        CreatedBy: record,
      };

      handleMessageUpdate(newMessage, client, [
        tenantID,
        view?.id,
        chatID,
        "BrieGPTMessages",
      ]);

      if (!providedMessage) {
        messageRef.current!.value = "";
      }

      flushSync(() => setProcessing(true));

      if (!chatID) {
        let createData;

        try {
          createData = await BrieConversationAPI.createBrieGPTChat({
            type: "view",
            viewID: view?.id,
            message: message,
            projectID: projectID || undefined,
          }).then((res) => res.data.data);
        } catch (err) {
          newToastEvent("error", getErrorMessage(err));
          setProcessing(false);
        }

        finalChatID = createData?.chatID;

        if (projectID) {
          client.refetchQueries({
            queryKey: ["BrieGPTProject", tenantID, projectID],
          });

          InvalidateGPTProjectQueries({
            tab: tab,
            tenantID,
            itemID: view?.id,
            client,
          });
        } else {
          client.invalidateQueries({
            queryKey: [tenantID, view?.id, "BrieGPTChats"],
          });
        }

        handleMessageUpdate(newMessage, client, [
          tenantID,
          view?.id,
          finalChatID,
          "BrieGPTMessages",
        ]);
        client.removeQueries({
          queryKey: [tenantID, view?.id, null, "BrieGPTMessages"],
        });

        params.set("chatID", finalChatID!);
        setParams(params);
      }

      const finalToken =
        Cookies.get(`authenticationToken_${TenantAPI.getCurrentTenant()}`) ||
        "undefined";

      const response = await fetch(
        `${window.GC_API_URL}/tenant/brie-conversations/brieGPT/chat`,
        {
          method: "POST",
          headers: {
            "app-id": "GrowthCloud",
            "Content-Type": "application/json",
            Authorization: `Bearer ${finalToken}`,
          },
          body: JSON.stringify({
            type: "view",
            message: message,
            model: model,
            chatID: finalChatID,
            viewID: view?.id,
            projectID: projectID,
            attachments: messageAttachments,
            timeZone: dayjs.tz.guess(),
          }),
          signal: signal,
          credentials: "include",
        }
      );

      if (!response.ok || !response.body) {
        let res: any;

        try {
          res = await response.json();
        } catch {}

        const errorMessage: any = {
          Content: getErrorMessage(
            res,
            "Something went wrong. Please try again."
          ),
          Role: "assistant",
          ChatID: chatID || "",
          ViewID: view?.id,
          ModelName: finalModel,
        };
        handleMessageUpdate(errorMessage, client, [
          tenantID,
          view?.id,
          finalChatID,
          "BrieGPTMessages",
        ]);

        return setProcessing(false);
      }

      // If the server is returning in streaming mode, response.body is a ReadableStream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let responseText = "";

      let filesReset = false;
      while (true) {
        let value;
        let done = false;

        try {
          const result = await reader.read();
          value = result.value;
          done = result.done;
          if (!filesReset) {
            setFiles([]);
            filesReset = true;
          }
        } catch (err) {
          if (controllerRef.current?.signal.aborted) {
            responseText += "\n\n[Stopped Processing]\n";
            setResponseMessage("");

            const stoppedMessage: any = {
              Content: responseText,
              Role: "assistant",
              ChatID: finalChatID || "",
              ViewID: view?.id,
              ModelName: finalModel,
            };
            handleMessageUpdate(stoppedMessage, client, [
              tenantID,
              view?.id,
              finalChatID,
              "BrieGPTMessages",
            ]);

            return setProcessing(false);
          }

          const errorMessage: any = {
            Content: "Something went wrong. Please try again.",
            Role: "assistant",
            ChatID: finalChatID || "",
            ViewID: view?.id,
            ModelName: finalModel,
          };
          handleMessageUpdate(errorMessage, client, [
            tenantID,
            view?.id,
            finalChatID,
            "BrieGPTMessages",
          ]);

          setResponseMessage("");
          return setProcessing(false);
        }

        let chunk = decoder.decode(value);

        if (done || chunk.endsWith("[DONE]")) {
          if (chunk.endsWith("[DONE]")) {
            chunk = chunk.replace("[DONE]", "");
            responseText += chunk;
          }

          setResponseMessage("");

          const doneMessage: any = {
            Content: responseText,
            Role: "assistant",
            ChatID: finalChatID || "",
            ViewID: view?.id,
            ModelName: finalModel,
          };
          handleMessageUpdate(doneMessage, client, [
            tenantID,
            view?.id,
            finalChatID,
            "BrieGPTMessages",
          ]);
          break;
        }

        responseText += chunk;
        setResponseMessage(responseText);
      }

      setProcessing(false);
    },
    [
      files,
      processing,
      model,
      chatID,
      view?.id,
      tenantID,
      user?.id,
      params,
      client,
    ]
  );

  const onStop = useCallback(() => {
    controllerRef.current?.abort();
    setProcessing(false);
  }, []);

  const chatName = useMemo(() => {
    return chatID
      ? projectID
        ? project?.Chats?.find((c) => c.id === chatID)?.Name
        : chats?.find((c) => c.id === chatID)?.Name
      : "New Chat";
  }, [projectID, chatID, chats, project]);

  // Modified firstMessageRef to only observe after initial scroll
  const firstMessageRef = useCallback(
    (node: HTMLDivElement) => {
      if (messagesLoading || !hasNextPage) return;

      // KEY FIX: Only observe after we've done the initial scroll
      if (!hasInitiallyScrolledRef.current || isInitialLoadRef.current) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !fetching) {
            // Additional check: only fetch if user has scrolled up significantly
            if (containerRef.current) {
              const container = containerRef.current;
              const scrollPercentage =
                (container.scrollTop /
                  (container.scrollHeight - container.clientHeight)) *
                100;

              // Only fetch if we're in the top 20% of the scroll area
              if (scrollPercentage < 20) {
                prevScrollHeightRef.current = container.scrollHeight;
                shouldPreserveScrollRef.current = true;
                fetchNextPage();
              }
            }
          }
        },
        {
          rootMargin: "50px 0px", // Reduced from 100px to make it less aggressive
          threshold: 0.05, // Reduced threshold
        }
      );

      if (node) observer.current.observe(node);
    },
    [
      fetchNextPage,
      hasNextPage,
      messagesLoading,
      fetching,
      hasInitiallyScrolledRef.current,
    ]
  );

  return (
    <div
      className={`tw:flex ${
        open ? "ml-2" : "tw:-mx-4"
      } tw:md:-mx-6 gap-2 tw:h-full`}
    >
      <BrieGPTSidebar
        chatID={chatID}
        viewID={view?.id}
        type="view"
        processing={processing}
        open={open}
        setOpen={setOpen}
        tab={tab}
        setTab={setTab}
      />
      <div
        className={`tw:flex tw:flex-col tw:items-center tw:-mt-2 tw:flex-1 tw:h-[calc(100vh_-_190px)] ${
          headerOpen
            ? "tw:md:h-[calc(100vh_-_208px)] "
            : "tw:md:h-[calc(100vh_-_160px)]"
        }`}
      >
        {((project && chatID) || processing || chatID) && (
          <div className="tw:flex-row tw:w-full tw:max-w-[55rem] tw:min-h-[4.5rem]">
            <div className="tw:flex tw:w-full tw:justify-start tw:max-w-[55rem] tw:items-center tw:gap-2 tw:mt-2">
              {project && (
                <div
                  className={`tw:flex tw:gap-2 tw:items-center tw:hover:cursor-pointer tw:hover:text-primary`}
                  onClick={() => {
                    params.delete("chatID");
                    setParams(params);
                  }}
                >
                  <i className="mdi mdi-folder-zip tw:text-2xl tw:text-primary" />
                  <p className={"tw:m-0 tw:font-semibold"}>
                    {project?.Name} {chatName ? "-" : ""}
                  </p>
                </div>
              )}
              {!project && (
                <i className="mdi mdi-message-outline tw:text-2xl tw:text-primary" />
              )}
              <span className={classNames({ "tw:font-semibold": !project })}>
                {chatName}
              </span>
            </div>
            {project && (
              <div className="tw:pt-1">
                <MembersList project={project} />
              </div>
            )}
          </div>
        )}
        <div
          className="tw:flex tw:flex-col tw:justify-between tw:items-center tw:w-full tw:h-full tw:overflow-y-auto tw:pt-1 tw:pl-1 tw:pr-2"
          ref={containerRef}
        >
          <div className="tw:flex tw:flex-col tw:justify-between tw:max-w-4xl tw:w-full">
            <div
              className={`tw:flex tw:flex-col tw:px-0 tw:md:px-0 tw:pt-0 tw:md:pt-6 tw:pb-16 ${
                !isMobile
                  ? open
                    ? sidebarOpen
                      ? "tw:max-w-[calc(100vw_-_575px)]"
                      : "tw:max-w-[calc(100vw_-_385px)]"
                    : sidebarOpen
                    ? "tw:max-w-[calc(100vw_-_355px)]"
                    : "tw:max-w-[calc(100vw_-_175px)]"
                  : open
                  ? "tw:max-w-[calc(100vw_-_80px)]"
                  : "tw:max-w-[calc(100vw_-_27px)]"
              }`}
            >
              {(messagesLoading || fetching) && chatID && (
                <div className="tw:w-full tw:pt-20 tw:flex tw:justify-center">
                  <AppSpinner size="lg" />
                </div>
              )}
              {!messages?.length && !messagesLoading && (
                <>
                  {projectID && !chatID ? (
                    <ProjectHome
                      tab={tab}
                      setTab={setTab}
                      type="view"
                      viewID={view?.id}
                    />
                  ) : (
                    <NewConversationMessage />
                  )}
                </>
              )}
              {!messagesLoading &&
                messages.map((message, i) => {
                  const isBrie = message.Role === "assistant";

                  if (!isBrie && message.Role !== "user") return null;

                  if (isBrie) {
                    return (
                      <Suspense fallback={null} key={i}>
                        <BrieComment
                          comment={message}
                          css={"tw:justify-start tw:max-w-full tw:w-full"}
                          innerCSS={"tw:w-full tw:px-3"}
                          backgroundColor={"transparent"}
                          copyButtonCSS={"tw:-mt-5 tw:justify-start"}
                          ref={i === 0 ? firstMessageRef : undefined}
                        />
                      </Suspense>
                    );
                  }

                  return (
                    <Suspense fallback={null} key={i}>
                      <BrieComment
                        comment={message}
                        css={"tw:justify-end tw:mb-4"}
                        backgroundColor={"#F0F3F2"}
                        maxWidth={650}
                        innerCSS={"tw:px-3 btn-oauth"}
                        useMarkdown={false}
                        setOpenFile={setOpenFile}
                        ref={i === 0 ? firstMessageRef : undefined}
                      />
                    </Suspense>
                  );
                })}
              {processing && !responseMessage && (
                <div className="tw:text-sm flow-text tw:font-medium tw:max-w-full tw:pl-4 tw:pt-4">
                  <span className="flow-text-inner">
                    BrieGPT is processing...
                  </span>
                </div>
              )}
              {responseMessage && (
                <Suspense fallback={null}>
                  <BrieComment
                    comment={{
                      Content: responseMessage,
                      Role: "assistant",
                      ChatID: chatID || "",
                      ViewID: view?.id,
                      ModelName: model,
                      CreatedBy: record,
                    }}
                    css="tw:justify-start tw:max-w-full tw:w-full"
                    innerCSS={"tw:w-full tw:px-3"}
                    backgroundColor="transparent"
                  />
                </Suspense>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
          {!isAtBottom && (
            <div
              className="tw:fixed tw:bottom-56 tw:rounded-full tw:border tw:border-gray-300 btn-oauth tw:w-8 tw:h-8 tw:flex tw:justify-center tw:cursor-pointer tw:hover:bg-superlight tw:hover:text-dark tw:bg-white"
              onClick={() => {
                scrollToBottom();
              }}
            >
              <i className="mdi mdi-chevron-down tw:leading-8 tw:text-2xl" />
            </div>
          )}
          <div className="tw:sticky tw:bottom-0 tw:-mx-2 tw:md:-mx-0 tw:max-w-4xl tw:w-full tw:z-1">
            <BrieGPTInput
              files={files}
              setFiles={setFiles}
              messageRef={messageRef}
              model={model}
              setModel={setModel}
              processing={processing}
              handleSendMessage={handleSendMessage}
              onStop={onStop}
              chatID={chatID}
            />
          </div>
        </div>
      </div>
      <GalleryPreviewModal
        record={null}
        deleteFile={undefined}
        nextFile={undefined}
        previousFile={undefined}
        setSelected={undefined}
        // @ts-expect-error
        files={openFile ? [openFile] : []}
        selected={0}
        isOpen={!!openFile}
        submitting={updatingFile}
        closeModal={() => setOpenFile(null)}
        handleUpdateFile={handleUpdateFile}
        setOpenInfo={setOpenInfo}
        openInfo={openInfo}
      />
    </div>
  );

  function handleUpdateFile(
    newValue: string,
    key: string,
    onSuccess?: () => void
  ) {
    if (!openFile?.File?.RecordID) return;

    updateFile({
      payload: { [key]: newValue },
      fileID: openFile?.File?.RecordID,
      onSuccess: async () => {
        setOpenFile((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            File: { ...prev?.File, [key]: newValue },
          };
        });

        client.invalidateQueries({
          queryKey: [tenantID, view?.id, chatID, "BrieGPTMessages"],
        });

        onSuccess && onSuccess();
      },
    });
  }
}

export default PageViewBrieGPT;
